<template>
  <div v-cloak id="pageContainer" class="pageContainer">
    <edit-scheduled-entries-modal
      name="edit-scheduled-entries"
      :entries="entries" />
    <tabs-portlet title="Scheduler"
                  :caption="userTimezone"
                  pre-caption="Timezone:"
                  caption-style="font-weight: bold; font-size: bigger"
                  icon="flaticon-event-calendar-symbol"
                  :icon-style="{
                    color: sass_vars['primary']
                  }"
                  :data-tabs="getSchedulerTabs()">
      <!-- portlet global actions -->
      <template #_actions>
        <csv-export />
        <a v-perms="['web.scheduler.create']" href="/scheduler/create" class="btn btn-ss-standout"><i class="flaticon2-plus" />New Event</a>
      </template>

      <!-- full calendar view -->
      <template #calendar>
        <calendar-view />
      </template>

      <!-- one-off -->
      <template #one-off-events>
        <f-data-table id="one-off-table"
                      :data-uri="dataUri"
                      heading="One-off events"
                      :columns="columns_once"
                      :selectable-rows="true"
                      :static-params="[
                        {
                          field: `forget`,
                          value: `true`
                        },
                        {
                          field: `ttype`,
                          value: `once`,
                        },
                      ]"
                      icon="flaticon2-calendar-7">
          <template #_actions="props">
            <button class="btn btn-ss" @click="props.reloadData">
              <i class="flaticon2-refresh" />Reload Data
            </button>
          </template>
          <template #_filters>
            <f-data-table-filters target="one-off-table" :expanded="false" :form-columns="2">
              <template #showname="props">
                <f-data-table-filter field="showname" display="Name:" :scope="props" />
              </template>
              <template #streamname="props">
                <f-data-table-filter field="streamname" display="Stream name:" :scope="props" />
              </template>
              <template #runat="props">
                <label for="js-begin_from_dateTime">Starts at:</label>
                <datetime id="js-begin_from_dateTime" v-model="start_date_filter"
                          :auto="true" :min-datetime="Date.now().toString()"
                          input-class="form-control kt-input" type="datetime"
                          format="yyyy-MM-dd HH:mm:ss" @close="() => {
                            props.filterChange({
                              client: {
                                value: formatDateTime(start_date_filter),
                                op: `gte`,
                                field: `runat`
                              }
                            })
                          }" />
              </template>
              <template #broadcaststop="props">
                <label for="js-begin_from_dateTime">Stops at:</label>
                <datetime id="js-begin_from_dateTime" v-model="stop_date_filter"
                          :auto="true" :min-datetime="Date.now().toString()"
                          input-class="form-control kt-input" type="datetime"
                          format="yyyy-MM-dd HH:mm:ss" @close="() => {
                            props.filterChange({
                              client: {
                                value: formatDateTime(stop_date_filter),
                                op: `lte`,
                                field: `runat`
                              }
                            })
                          }" />
              </template>
              <template #status="props">
                <f-data-table-filter>
                  <label for="status-filter">Status:</label>
                  <multiselect v-model="once_ms_val"
                               :options="[
                                 { display: 'Scheduled', val: 'SCH' },
                                 { display: 'Complete', val: 'RAN' },
                                 { display: 'Error', val: 'ERR' },
                                 { display: 'Validating', val: 'NEW' }
                               ]"
                               track-by="val"
                               label="display"
                               placeholder="Filter status..."
                               @input="(selected) => {
                                 props.filterChange({
                                   client: {
                                     op: (status) => {
                                       if (!selected) {
                                         return true;
                                       }
                                       return selected.val === status;
                                     },
                                     field: `status`
                                   }
                                 });
                               }" />
                </f-data-table-filter>
              </template>
            </f-data-table-filters>
          </template>
          <template #_row_controls="props">
            <button class="btn btn-ss" @click="{ props.do({ cb: editScheduledEntries }) }">
              <i class="fa fa-pen" /> Edit
            </button>
            <button class="btn btn-ss-danger" @click="{ props.do({ cb: destroyScheduledEntries }) }">
              <i class="flaticon2-warning" /> Delete
            </button>
          </template>
          <template #image="props">
            <img :src="props.row.image" style="max-height: 70px; width: auto;">
          </template>
          <template #status="props">
            <span>{{ props.row.status | friendly_status_once }}</span>
          </template>
          <template #runat="props">
            {{ formatDateTime(props.row.runat) }}
          </template>
          <template #broadcaststop="props">
            {{ formatDateTime(props.row.broadcaststop) }}
          </template>
          <template #tags="props">
            <multi-select-tag-bag v-if="props.row.tags" :tags-list="props.row.tags.split(',')" :limit="5" />
            <span v-else class="muted">None</span>
          </template>
        </f-data-table>
      </template>
      <!-- recurring -->
      <template #recurring-events>
        <f-data-table id="recurring-table"
                      :data-uri="dataUri"
                      heading="Recurring events"
                      :columns="columns_cron"
                      :selectable-rows="true"
                      :static-params="[
                        {
                          field: `forget`,
                          value: `true`
                        },
                        {
                          field: `ttype`,
                          value: `cron`,
                        }
                      ]"
                      icon="flaticon2-calendar-9">
          <template #_actions="props">
            <button class="btn btn-ss" @click="props.reloadData">
              <i class="flaticon2-refresh" />Reload Data
            </button>
          </template>
          <template #_filters>
            <f-data-table-filters target="recurring-table" :expanded="false" :form-columns="2">
              <template #showname="props">
                <f-data-table-filter field="showname" display="Name:" :scope="props" />
              </template>
              <template #streamname="props">
                <f-data-table-filter field="streamname" display="Stream name:" :scope="props" />
              </template>
              <template #runat="props">
                <label for="js-begin_from_dateTime">Starts at:</label>
                <datetime id="js-begin_from_dateTime" v-model="start_date_filter"
                          :auto="true" :min-datetime="Date.now().toString()"
                          input-class="form-control kt-input" type="datetime"
                          format="yyyy-MM-dd HH:mm:ss" @close="() => {
                            props.filterChange({
                              client: {
                                value: formatDateTime(start_date_filter),
                                op: `gte`,
                                field: `runat`
                              }
                            })
                          }" />
              </template>
              <template #broadcaststop="props">
                <label for="js-end_from_dateTime">Stops at:</label>
                <datetime id="js-end_from_dateTime" v-model="stop_date_filter"
                          :auto="true" :min-datetime="Date.now().toString()"
                          input-class="form-control kt-input" type="datetime"
                          format="yyyy-MM-dd HH:mm:ss" @close="() => {
                            props.filterChange({
                              client: {
                                value: formatDateTime(stop_date_filter),
                                op: `lte`,
                                field: `runat`
                              }
                            })
                          }" />
              </template>
              <template #status="props">
                <f-data-table-filter>
                  <label for="status-filter">Status:</label>
                  <multiselect v-model="once_ms_val"
                               :options="[
                                 { display: 'Active', val: 'SCH' },
                                 { display: 'Complete', val: 'RAN' },
                                 { display: 'Error', val: 'ERR' },
                                 { display: 'Validating', val: `NEW` }
                               ]"
                               track-by="val"
                               label="display"
                               placeholder="Filter status..."
                               @input="(selected) => {
                                 props.filterChange({
                                   client: {
                                     op: (status) => {
                                       if (!selected) {
                                         return true;
                                       }
                                       return selected.val === status;
                                     },
                                     field: `status`
                                   }
                                 });
                               }" />
                </f-data-table-filter>
              </template>
            </f-data-table-filters>
          </template>
          <template #_row_controls="props">
            <button class="btn btn-ss" @click="{ props.do({ cb: editScheduledEntries }) }">
              <i class="fa fa-pen" /> Edit
            </button>
            <button class="btn btn-ss-danger" @click="{ props.do({ cb: destroyScheduledEntries }) }">
              <i class="flaticon2-warning" /> Delete
            </button>
          </template>
          <template #image="props">
            <img :src="props.row.image" style="max-height: 70px; width: auto;">
          </template>
          <template #status="props">
            <span>{{ props.row.status | friendly_status_recurring }}</span>
          </template>
          <template #duration="props">
            {{ humanDuration(props.row.duration) }}
          </template>
          <template #nextfiretime="props">
            <div>{{ props.row.nextfiretime }}</div>
            <p>
              Active until: {{ props.row?.endsat ?? 'Forever' }}
            </p>
          </template>
          <template #tags="props">
            <multi-select-tag-bag v-if="props.row.tags" :tags-list="props.row.tags.split(',')" :limit="5" />
            <span v-else class="muted">None</span>
          </template>
        </f-data-table>
      </template>

      <!-- quick recording -->
      <template #quick-record>
        <div class="kt-portlet__body">
          <div class="kt-section kt-section--first">
            <quick-record />
            <div class="card">
              <div class="card-header">
                Currently Recording
              </div>
              <div class="card-body">
                <currently-recording />
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #recently-recorded>
        <div class="kt-portlet__body">
          <div class="kt-section kt-section--first">
            <div class="card">
              <div class="card-header">
                Recently Recorded
              </div>
              <div class="card-body">
                <recently-recorded />
              </div>
            </div>
          </div>
        </div>
      </template>
    </tabs-portlet>

    <delete-confirm-modal :entities-to-delete="selected_events"
                          entity-type="Event">
      <div class="text-center" style="font-size: 1.1em;">
        {{ delete_message }}
      </div>
    </delete-confirm-modal>
    <quick-alert-modal v-bind="quick_alert" />
  </div>
</template>

<script>
import TabsPortlet from "VueComponents/TabsPortlet.vue";
import QuickRecord from "VuePartials/QuickRecord";
import CurrentlyRecording from "VuePartials/CurrentlyRecording";
import RecentlyRecorded from "VuePartials/Scheduler/RecentlyRecorded";
import CalendarView from "VuePartials/Scheduler/CalendarView";
import { formatDate } from "Helpers/formatters";
import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable.vue";
import FDataTableFilters from "VuePartials/App/Storage/FDataTable/FDataTableFilters.vue";
import FDataTableFilter from "VuePartials/App/Storage/FDataTable/FDataTableFilter.vue";
import CsvExport from "VuePartials/Scheduler/CsvExport";
import MultiSelectTagBag from 'VueComponents/MultiSelectTagBag';
import DeleteConfirmModal from 'VueModals/DeleteConfirmModal';
import EditScheduledEntriesModal from 'VueModals/EditScheduledEntriesModal.vue';
import sass_vars from 'AssetsSass/_variables.scss';
import SchedulerEditMixin from 'VueMixins/SchedulerEditMixin';

export default {
  components: {
    CsvExport,
    CalendarView,
    QuickRecord,
    CurrentlyRecording,
    FDataTable,
    FDataTableFilters,
    FDataTableFilter,
    TabsPortlet,
    RecentlyRecorded,
    MultiSelectTagBag,
    DeleteConfirmModal,
    EditScheduledEntriesModal,
  },
  filters: {
    schedule_type(value) {
      if (value === 'cron') {
        return 'Recurring';
      }
      if (value === 'once') {
        return 'One-off';
      }

      return value;
    },
    friendly_status_once(value) {
      if (value === 'NEW') {
        return 'Validating';
      }

      if (value === 'SCH') {
        return 'Scheduled';
      }

      if (value === 'RAN') {
        return 'Complete';
      }

      if (value === 'ERR') {
        return 'Error';
      }

      return value;
    },

    friendly_status_recurring(value) {
      if (value === 'NEW') {
        return 'Validating';
      }

      if (value === 'SCH') {
        return 'Active';
      }

      if (value === 'RAN') {
        return 'Complete';
      }

      if (value === 'ERR') {
        return 'Error';
      }

      return value;
    },
  },
  mixins: [SchedulerEditMixin],
  data: () => ({
    selected_events: [],
    userTimezone: window.user?.timezone,
    start_date_filter: '',
    stop_date_filter: '',
    active: false,
    timezoneSelected: {
      zone: 'UTC',
    },
    timezones: window.timezones,
    user: window.user,
    sortOptionsOnce: {
      enabled: true,
      initialSortBy: { field: "runat", type: "asc" },
    },
    sortOptionsCron: {
      enabled: true,
      initialSortBy: { field: "nextfiretime", type: "asc" },
    },
    paginationOptions: {
      enabled: true,
      perPage: 10,
    },
    searchOptions: {
      enabled: true,
      trigger: "enter",
    },
    selectOptions: {
      enabled: true,
    },

    dataUri: `/api/v1/scheduler`,
    columns_once: [
      {
        display: "Image",
        field: "image",
        width: 90,
        sortable: false,
      },
      {
        display: "Name",
        field: "showname",
      },
      {
        display: "Status",
        field: "status",
      },
      {
        display: "Tags",
        field: "tags",
      },
      {
        display: "Stream",
        field: "streamname",
      },
      {
        display: "Starts at",
        field: "runat",
        default_sort: true,
      },
      {
        display: "Stops at",
        field: "broadcaststop",
      },
    ],
    once_ms_val: ``,

    columns_cron: [
      {
        display: "Image",
        field: "image",
        sortable: false,
      },
      {
        display: "Name",
        field: "showname",
        default_sort: true,
      },
      {
        display: "Status",
        field: "status",
      },
      {
        display: "tags",
        field: 'tags',
      },
      {
        display: "Stream",
        field: "streamname",
      },
      {
        display: "Next Fire",
        field: "nextfiretime",
      },
      {
        display: "Duration",
        field: "duration",
      },
    ],

    rowsOnce: [],
    rowsCron: [],
    streams: [],
    stream: "",
  }),

  computed: {
    delete_message() {
      const is_recurring = this.selected_events[0]?.ttype === `cron`;
      const plural = this.selected_events.length > 1;
      const determiner = plural ? `these` : `this`;
      const subject = plural || is_recurring ? `events` : `event`;
      const recur_clarity = this.selected_events[0]?.ttype === `cron` ? `and all recurring` : ``;
      return `This will permanently delete ${determiner} ${recur_clarity} ${subject}!`;
    },
  },

  created() {
    this.sass_vars = sass_vars;
  },

  beforeMount() {
    if (!window.cookies.isKey('scheduler.index_tab')) {
      window.cookies.set('scheduler.index_tab', 'calendar', '1y');
    }
  },

  mounted() {
    // Rare case timezone is null or empty, notify if timezone is empty.
    // It should default to UTC
    if (_.isEmpty(this.userTimezone)) {
      this.quickAlert({
        title: `Timezone is not set`,
        message: `Please log out and log back in or set it manually in preferences. If issue persists, contact support.`,
        variant: `error`,
      });
    }

    window.EventBus.$on('tab-requested', (payload) => {
      this.selected_events = [];
      window.cookies.set('scheduler.index_tab', payload, '1y');
      if (payload === 'calendar') {
        window.dispatchEvent(new Event('resize')); // trigger resize to render calendar if it is not initial tab (workaround!)
      }
    });
    EventBus.$on(`fdt-rows-selected`, (ev) => {
      this.selected_events = ev;
    });
  },

  methods: {
    formatDate,
    getSchedulerTabs() {
      const tabs = [
        {
          name: `calendar`,
          display: `Calendar`,
        },
        {
          name: `one-off-events`,
          display: `One-off Events`,
          lazy: true,
        },
        {
          name: `recurring-events`,
          display: `Recurring Events`,
          lazy: true,
        },
        {
          name: 'recently-recorded',
          display: 'Recently Recorded',
        },
      ];

      if (this.permissionsCanAccessFeature('recorder')) {
        tabs.push({
          name: `quick-record`,
          display: `Quick Record`,
        });
      }

      // get value from cookie
      const index = tabs.findIndex(tab => tab.name === window.cookies.get('scheduler.index_tab') ?? 'calendar');
      tabs.splice(index, 1, { ...tabs[index], active: true });

      // set tabs route (used for cookie to identify the page (if cookies are used))
      tabs.map(tab => tab.route = 'scheduler.index');

      return tabs;
    },

    /**
     * Returns a better format to display for duration
     */
    humanDuration(duration) {
      return moment.duration(parseInt(duration), "seconds").format("h [hrs] m [min]");
    },

    async fetchEvents(forget) {
      const response = await axios.get('/api/v1/scheduler', {
        params: {
          'forget': forget,
        },
      });

      this.rowsOnce = _.filter(response.data.data, { 'ttype': 'once' });
      this.rowsCron = _.filter(response.data.data, { 'ttype': 'cron' });
    },

    async updateData() {
      this.fetchEvents(1);
    },

    async destroyScheduledEntries(...entries) {
      const response = await this.fireModal(`delete-confirm`);

      if (!response.data.confirm) {
        return false;
      }

      const ids = entries.map(entry => entry.uuid).join(`,`);
      try {
        const response = await axios.delete(`/api/v1/scheduler`, {
          params: { 'ids': ids },
        });
        if (response.data.success) {
          if (response.data.data.response.original.status === "partial") {
            toastr.warning(`Some entries couldn't be deleted`);
          } else {
            toastr.success(
              "All selected entries have been deleted!",
            );
          }
        } else {
          toastr.error("Oops. Something went wrong!");
        }
        this.updateData();
      } catch (error) {
        for (const e of error.response.data.response.errors) {
          toastr.error(e);
        }
        toastr.error("Oops. Something went wrong.");
        this.updateData();
      }
    },
  },

};
</script>
